import { useTranslation } from "@vixi/localization";
import { ComponentProps, useCallback, useMemo } from "react";
import { PaginationNavProps } from ".";

// Assets
import DeleteIcon from "@assets/icons/delete.svg?react";
import PaginationArrowhead from "@assets/icons/pagination-arrowhead.svg?react";
import { genArray } from "@vixi/utils";
import { Button, ButtonProps } from "../base/button";
import { cn } from "@/lib/utils";
import { useNavigate } from "@vixi/router";

const paginationWithDeleteClasses = {
  root: "PaginationWithDelete-root",
  navItem: "PaginationWithDelete-navItem",
  navButton: "PaginationWithDelete-navButton",
  navButtonDir: (dir: "start" | "end") => `PaginationWithDelete-navButton-${dir}`,
} as const;

const PaginationNavButton = ({ ...props }: ButtonProps) => (
  <Button
    size="icon"
    variant="text"
    color="primary"
    {...props}
    className={cn(
      paginationWithDeleteClasses.navButton,
      "text-primary-light disabled:text-primary-light hover:text-primary size-9 h-auto p-0.75 [&_svg]:size-full",
      props.className,
    )}
  />
);

const PageNavItem = ({
  variant,
  ...props
}: ComponentProps<"span"> & { variant: "contained" | "text" }) => (
  <span
    {...props}
    className={cn(
      paginationWithDeleteClasses.navItem,
      "fs-14 text-gray box-border flex min-h-8.75 min-w-7 items-center justify-center rounded leading-none font-bold select-none",
      variant === "contained" && "fs-18 border-primary-light bg-primary-light border-3 text-black",
      variant === "text" &&
        "text-gray hover:bg-primary-light/40 bg-transparent transition-colors duration-200 ease-in-out hover:text-black",
      props.className,
    )}
  />
);

interface DeleteButtonProps extends ButtonProps {}

const DeleteButton = (props: DeleteButtonProps) => {
  return (
    <Button
      variant="contained"
      size="pill"
      color="paper"
      {...props}
      className={cn(
        "hover:text-destructive disabled:hover:text-foreground block h-full px-2 transition-colors duration-200 ease-in-out disabled:cursor-not-allowed disabled:opacity-70",
        props.className,
      )}
    />
  );
};

interface PaginationWithDeleteProps extends PaginationNavProps {
  deleteDisabled?: boolean;
  onDeleteItem: (page: number, event: React.MouseEvent<HTMLButtonElement>) => void;
}

function PaginationWithDelete({
  activePage: activeItem,
  totalPages: itemCount,
  onGoToPage: onGoToItem,
  visiblePages = 5,
  onDeleteItem,
  ...props
}: PaginationWithDeleteProps) {
  const { tNum, isRtl } = useTranslation();
  const navigate = useNavigate();

  const firstVisibleItem = activeItem - Math.floor(visiblePages / 2);
  const isInRange = useCallback(
    (item: number) => {
      return (
        item >= Math.max(0, firstVisibleItem) &&
        item < Math.min(itemCount, firstVisibleItem + visiblePages)
      );
    },
    [firstVisibleItem, visiblePages, itemCount],
  );

  const goTo = useMemo(() => {
    if (onGoToItem) {
      return (n: number) => {
        // Prevent going beyond the last item
        if (n >= itemCount) {
          return;
        }
        // Prevent going before the first item
        if (n < 0) {
          return;
        }
        onGoToItem(n);
      };
    } else {
      return (n: number) => {
        // Prevent going beyond the last item
        if (n >= itemCount) {
          return;
        }
        // Prevent going before the first item
        if (n < 0) {
          return;
        }
        navigate({
          to: ".",
          replace: true,
          search: {
            pageNumber: n,
          },
        });
      };
    }
  }, [onGoToItem, itemCount]);

  const hasPrev = props.hasPreviousPage ?? activeItem > 0;
  const hasNext = props.hasNextPage ?? activeItem < itemCount - 1;

  const itemsArray = genArray((i) => i + firstVisibleItem, visiblePages);

  return (
    <div className={cn("my-1.5 box-border flex w-full items-center justify-between px-2.5", isRtl && "flex-row-reverse")}>
      <PaginationNavButton
        className={paginationWithDeleteClasses.navButtonDir("start")}
        disabled={!hasPrev}
        onClick={() => goTo(activeItem - 1)}
      >
        <PaginationArrowhead className="size-full" />
      </PaginationNavButton>

      <div className="flex items-center gap-1">
        {itemsArray.map((item, i) =>
          item === activeItem ? (
            // Active item
            <PageNavItem key={i} variant={"contained"}>
              <span style={{ paddingInline: 10 }}>{tNum(item + 1)}</span>
              <DeleteButton
                disabled={itemCount < 2 || props.deleteDisabled}
                onClick={(event) => {
                  if (item === itemCount - 1) goTo(activeItem - 1);
                  onDeleteItem(item, event);
                }}
              >
                <DeleteIcon className="size-5" />
              </DeleteButton>
            </PageNavItem>
          ) : isInRange(item) ? (
            // Inactive item
            <Button key={i} onClick={() => goTo(item)} size="content" variant={"text"}>
              <PageNavItem variant={"text"}>{tNum(item + 1)}</PageNavItem>
            </Button>
          ) : (
            <div className="h-8.75 w-7" key={i} />
          ),
        )}
      </div>

      <PaginationNavButton disabled={!hasNext} onClick={() => goTo(activeItem + 1)}>
        <PaginationArrowhead className="size-full -scale-x-100" />
      </PaginationNavButton>
    </div>
  );
}

export { PaginationWithDelete, paginationWithDeleteClasses };
export type { PaginationWithDeleteProps };
